import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import Axios from "../../api-utility/axiosInstance";

export const getSummary = createAsyncThunk(
  "dashboard/summary",
  async (_, { rejectWithValue }) => {
    try {
      const response = await Axios.get("/tenant/dashboard/summary");

      if (response.data?.success === false) {
        return rejectWithValue(
          response.data.message || "Failed to get summary",
        );
      }

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to get summary",
      );
    }
  },
);

export const getTasks = createAsyncThunk(
  "dashboard/tasks",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await Axios.get("/tenant/dashboard/todo-list", {
        params,
      });

      if (response.data?.success === false) {
        return rejectWithValue(response.data.message || "Failed to get tasks");
      }

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to get tasks",
      );
    }
  },
);

export const getLeadStatus = createAsyncThunk(
  "dashboard/lead-status",
  async (_, { rejectWithValue }) => {
    try {
      const response = await Axios.get("/tenant/dashboard/lead-status");

      if (response.data?.success === false) {
        return rejectWithValue(
          response.data.message || "Failed to get lead status",
        );
      }

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to get lead status",
      );
    }
  },
);

export const getLeadSource = createAsyncThunk(
  "dashboard/lead-source",
  async (_, { rejectWithValue }) => {
    try {
      const response = await Axios.get("/tenant/dashboard/lead-source");

      if (response.data?.success === false) {
        return rejectWithValue(
          response.data.message || "Failed to get lead source",
        );
      }

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to get lead source",
      );
    }
  },
);

export const getOverview = createAsyncThunk(
  "dashboard/overview",
  async (_, { rejectWithValue }) => {
    try {
      const response = await Axios.get("/tenant/dashboard/overview");

      if (response.data?.success === false) {
        return rejectWithValue(
          response.data.message || "Failed to get overview",
        );
      }

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to get overview",
      );
    }
  },
);

// Sales card for one period: "month" | "quarter" | "year"
export const getSales = createAsyncThunk(
  "dashboard/sales",
  async (period, { rejectWithValue }) => {
    try {
      const response = await Axios.get("/tenant/dashboard/sales", {
        params: { period },
      });

      if (response.data?.success === false) {
        return rejectWithValue(response.data.message || "Failed to get sales");
      }

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to get sales",
      );
    }
  },
);

// Focused dashboards: view = "sales" | "pipeline" | "activities" | "team",
// params = { period: "month" | "quarter" | "year", assigned_to }
export const getInsight = createAsyncThunk(
  "dashboard/insight",
  async ({ view, params = {} }, { rejectWithValue }) => {
    try {
      const response = await Axios.get(`/tenant/dashboard/insights/${view}`, {
        params,
      });

      if (response.data?.success === false) {
        return rejectWithValue(
          response.data.message || "Failed to get dashboard",
        );
      }

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to get dashboard",
      );
    }
  },
);

export const getGlobalData = createAsyncThunk(
  "dashboard/global-data",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await Axios.get("/tenant/dashboard/global-data", {
        params,
      });

      if (response.data?.success === false) {
        return rejectWithValue(
          response.data.message || "Failed to get global data",
        );
      }

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to get global data",
      );
    }
  },
);

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState: {
    summary: {},
    tasks: {},
    leadStatus: {},
    leadSource: {},
    summaryLoading: false,
    tasksLoading: false,
    leadStatusLoading: false,
    leadSourceLoading: false,
    overview: {},
    // true until the first load, so the cards open with placeholders instead of zeros
    overviewLoading: true,
    // Sales card data per period (quarter / year); month comes with the overview
    sales: {},
    salesLoading: false,
    // Focused dashboards, keyed by view; each keeps the filters it was loaded with
    insights: {},
    // all / lower_levels / own: shows the Team dashboard and the person filter when not "own"
    reportScope: null,
    insightLoading: {},
    insightError: {},
    globalData: {},
    globalDataLoading: false,
    error: null,
  },

  reducers: {
    clearResults: (state) => {
      state.globalData = {};
      state.globalDataLoading = false;
    },
  },

  extraReducers: (builder) => {
    builder
      //GET SUMMARY
      .addCase(getSummary.pending, (state) => {
        state.summaryLoading = true;
        state.error = null;
      })
      .addCase(getSummary.fulfilled, (state, action) => {
        state.summaryLoading = false;
        state.summary = action.payload?.data || {};
      })
      .addCase(getSummary.rejected, (state, action) => {
        state.summaryLoading = false;
        state.error = action.payload || "Failed to fetch summary";
      })

      //GET TASKS
      .addCase(getTasks.pending, (state, action) => {
        if (!action.meta.arg?._loadMore) {
          state.tasksLoading = true;
        }
        state.error = null;
      })
      .addCase(getTasks.fulfilled, (state, action) => {
        state.tasksLoading = false;
        
        const loadMoreCategory = action.meta.arg?._loadMore;
        const newData = action.payload?.data || {};

        if (loadMoreCategory) {
          if (!state.tasks) state.tasks = {};
          
          if (["overdue", "today", "next"].includes(loadMoreCategory)) {
             if (state.tasks.pending && newData.pending) {
                const existingItems = state.tasks.pending[loadMoreCategory]?.items || [];
                const newItems = newData.pending[loadMoreCategory]?.items || [];
                
                state.tasks.pending[loadMoreCategory] = {
                   ...newData.pending[loadMoreCategory],
                   items: [...existingItems, ...newItems]
                };
             }
          } else if (["done", "cancelled"].includes(loadMoreCategory)) {
             if (state.tasks[loadMoreCategory] && newData[loadMoreCategory]) {
                const existingItems = state.tasks[loadMoreCategory]?.items || [];
                const newItems = newData[loadMoreCategory]?.items || [];
                
                state.tasks[loadMoreCategory] = {
                   ...newData[loadMoreCategory],
                   items: [...existingItems, ...newItems]
                };
             }
          }
        } else {
          state.tasks = newData;
        }
      })
      .addCase(getTasks.rejected, (state, action) => {
        state.tasksLoading = false;
        state.error = action.payload || "Failed to fetch tasks";
      })

      // GET LEAD STATUS
      .addCase(getLeadStatus.pending, (state) => {
        state.leadStatusLoading = true;
        state.selectedLead = null;
      })
      .addCase(getLeadStatus.fulfilled, (state, action) => {
        state.leadStatusLoading = false;
        state.leadStatus = action.payload?.data || {};
      })
      .addCase(getLeadStatus.rejected, (state, action) => {
        state.leadStatusLoading = false;
        state.error = action.payload;
      })

      // GET LEAD SOURCE
      .addCase(getLeadSource.pending, (state) => {
        state.leadSourceLoading = true;
        state.leadSource = [];
      })
      .addCase(getLeadSource.fulfilled, (state, action) => {
        state.leadSourceLoading = false;
        state.leadSource = action.payload?.data || {};
      })
      .addCase(getLeadSource.rejected, (state, action) => {
        state.leadSourceLoading = false;
        state.error = action.payload;
      })

      // GET OVERVIEW
      .addCase(getOverview.pending, (state, action) => {
        // Background refresh (after a task changes) keeps the numbers on screen
        if (!action.meta.arg?.silent) state.overviewLoading = true;
      })
      .addCase(getOverview.fulfilled, (state, action) => {
        state.overviewLoading = false;
        state.overview = action.payload?.data || {};
        state.reportScope = action.payload?.data?.report_scope || state.reportScope;
      })
      .addCase(getOverview.rejected, (state, action) => {
        state.overviewLoading = false;
        state.error = action.payload;
      })

      // GET SALES (one period)
      .addCase(getSales.pending, (state) => {
        state.salesLoading = true;
      })
      .addCase(getSales.fulfilled, (state, action) => {
        state.salesLoading = false;
        state.sales[action.meta.arg] = action.payload?.data || {};
      })
      .addCase(getSales.rejected, (state, action) => {
        state.salesLoading = false;
        state.error = action.payload;
      })

      // GET INSIGHT (one focused dashboard)
      .addCase(getInsight.pending, (state, action) => {
        const { view } = action.meta.arg;
        state.insightLoading[view] = true;
        state.insightError[view] = null;
      })
      .addCase(getInsight.fulfilled, (state, action) => {
        const { view } = action.meta.arg;
        state.insightLoading[view] = false;
        state.insights[view] = action.payload?.data || {};
        state.reportScope = action.payload?.data?.meta?.report_scope || state.reportScope;
      })
      .addCase(getInsight.rejected, (state, action) => {
        const { view } = action.meta.arg;
        state.insightLoading[view] = false;
        state.insightError[view] = action.payload || "Failed to get dashboard";
      })

      // GET GLOBAL DATA
      .addCase(getGlobalData.pending, (state) => {
        state.globalDataLoading = true;
        state.error = null;
      })
      .addCase(getGlobalData.fulfilled, (state, action) => {
        state.globalDataLoading = false;
        state.globalData = action.payload?.data || {};
      })
      .addCase(getGlobalData.rejected, (state, action) => {
        state.globalDataLoading = false;
        state.error = action.payload || "Failed to fetch global data";
      });
  },
});

export const { clearResults } = dashboardSlice.actions;

export default dashboardSlice.reducer;
